import type { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import axios from 'axios';

import { appConfig } from '@/config/appConfig';
import useAuthStore from '@/features/authentication/hooks/useAuthStore';

import useI18n from './useI18n';

type UseAxiosReturn = {
    axiosInstance: AxiosInstance;
};

export default function useAxios(): UseAxiosReturn {
    // ----------------------------------------------------------------------------------------------------
    // MARK: States & Constants
    // ----------------------------------------------------------------------------------------------------
    const { locale } = useI18n();
    const token = useAuthStore((state) => state.token);
    const resetStore = useAuthStore((state) => state.resetStore);

    const axiosInstance: AxiosInstance = axios.create({
        baseURL: appConfig.env.apiBaseUrl,
        headers: {
            Authorization: token ? `Bearer ${token.access_token}` : 'test',
            'Accept-Language': locale,
        },
    });

    // ----------------------------------------------------------------------------------------------------
    // Interceptor for 401 error handling
    // ----------------------------------------------------------------------------------------------------
    axiosInstance.interceptors.response.use(
        (response: AxiosResponse) => response,
        (error: AxiosError) => {
            if (axios.isAxiosError(error) && error.response?.status === 401) {
                resetStore();
            }
            return Promise.reject(error instanceof Error ? error : new Error('An unknown error occurred'));
        }
    );

    // ----------------------------------------------------------------------------------------------------
    // MARK: Return
    // ----------------------------------------------------------------------------------------------------
    return { axiosInstance };
}
